/**
 * pdf-to-jpg / app.js — the page controller.
 *
 * Two answers to "PDF → pictures":
 *   pages   one image per page (a single file, or several in an archive/folder)
 *   sheet   ALL selected pages in ONE image — a column (the long-screenshot
 *           look), a grid, or a row — with the gap, background, order and
 *           margins under the person's control.
 *
 * Why the second mode exists: 23.09.2026 a visitor converted a 14-page rental
 * contract, got a 33 MB archive of 14 JPGs, searched our site for "converter
 * em arquivo jpg unico", came back and clicked FOUR times on the line "Will
 * produce 14 images" — as if it were the switch he was looking for. It was not
 * there. Now the choice is the first thing in the rail, and the pages-mode
 * summary offers it right where he clicked.
 *
 * The long image is assembled in bands (compose.js) and streamed into an
 * encoder (jpeg-stream.js / png-stream.js), never onto one canvas: a canvas
 * stops at 8 192 px on an iPhone and at 32 767 in Chromium, and past its limit
 * it stays BLANK without an error. What the format itself cannot hold (a JPG
 * taller than 65 500 px) is refused BEFORE any work, with the nearest settings
 * that fit offered as one click.
 */

'use strict';

import { planSheet, checkPlan, suggestFit, formatRange, parseRange } from './sheet.js';
import { composeBands, parseHex } from './compose.js';
import * as Pages from './pages.js';
import { createEncoder as jpegEncoder } from '../jpeg-stream.js';
import * as PngStream from '../merge-images/png-stream.js';
import { canvasLimits, canvasFits } from '../canvas-limits.js';

const SLUG = 'pdf-to-jpg';
const CFG = window.__P2J || {};
const T = CFG.i18n || {};
const $ = (id) => document.getElementById(id);

function t(key, vars) {
    let s = T[key] != null ? String(T[key]) : key;
    if (vars) {
        for (const k of Object.keys(vars).sort((a, b) => b.length - a.length)) {
            s = s.split(':' + k).join(String(vars[k]));
        }
    }
    return s;
}
function ae(f, data) {
    try { if (typeof window.__ae === 'function') window.__ae('feature_use', Object.assign({ slug: SLUG, f }, data || {})); } catch (_) {}
}
function stage(s) { try { if (typeof window.__stage === 'function') window.__stage(s); } catch (_) {} }
function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (_) {} }
function breathe() {
    if (typeof scheduler !== 'undefined' && scheduler && typeof scheduler.yield === 'function') return scheduler.yield();
    return new Promise((r) => setTimeout(r, 0));
}
function humanSize(bytes) {
    if (!bytes) return '0 KB';
    if (bytes < 1024 * 1024) return Math.max(1, Math.round(bytes / 1024)) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}
const NF = (() => { try { return new Intl.NumberFormat(window.__LOCALE || undefined); } catch (_) { return null; } })();
function fmtInt(n) { return NF ? NF.format(Math.round(n)) : String(Math.round(n)); }
function baseName(s) { return String(s || 'document').replace(/\.[^.]+$/, '') || 'document'; }
function coded(message, code) { const e = new Error(message); e.code = code; return e; }

/* ── settings that outlive the visit (preferences, never content) ─────────── */
const SET = window.__toolSettings;
function loadSetting(k, fb) { try { return SET ? SET.load(SLUG, k, fb) : fb; } catch (_) { return fb; } }
function saveSetting(k, v) { try { if (SET) SET.save(SLUG, k, v); } catch (_) {} }

const BG_PRESETS = ['#d9d9d9', '#ffffff', '#1f2937'];

/* ── state ────────────────────────────────────────────────────────────────── */
const S = {
    file: null, name: '', doc: null,
    sizes: [],          // [{index, w, h}] points
    order: [],          // selected page indices, in sheet order
    mode: loadSetting('mode', 'pages') === 'sheet' ? 'sheet' : 'pages',
    layout: ['column', 'grid', 'row'].indexOf(loadSetting('layout', 'column')) >= 0 ? loadSetting('layout', 'column') : 'column',
    columns: Math.min(6, Math.max(2, Number(loadSetting('columns', 2)) || 2)),
    gapPt: Math.min(36, Math.max(0, Number(loadSetting('gap', 10)))),
    bg: /^#[0-9a-f]{6}$/i.test(loadSetting('bg', '')) ? loadSetting('bg', '') : BG_PRESETS[0],
    trim: loadSetting('trim', '0') === '1',
    ink: new Map(),     // page index -> box in points | null (blank)
    inkRun: 0,
    textPt: undefined,  // body text size in points; null = no text layer
    plan: null,
    busy: false, abort: null, token: null,
    result: null,       // { blob, name, w?, h? }
    pending: null,      // pages kept for the folder lane
    loadSeq: 0, gen: 0,
};

/* ── elements ─────────────────────────────────────────────────────────────── */
const E = {};
function grab() {
    for (const id of [
        'p2j-upload', 'p2j-workspace', 'p2j-mode-pages', 'p2j-mode-sheet', 'p2j-mode-pages-sub', 'p2j-mode-sheet-sub',
        'p2j-sheet-opts', 'p2j-layout', 'p2j-cols-f', 'p2j-cols', 'p2j-gap', 'p2j-gap-val', 'p2j-bg', 'p2j-bg-custom',
        'p2j-trim', 'p2j-format', 'p2j-dpi', 'p2j-quality', 'p2j-quality-val', 'p2j-quality-row', 'p2j-range',
        'p2j-range-err', 'p2j-output-name', 'p2j-more-sub', 'p2j-preview', 'p2j-read', 'p2j-warn', 'p2j-go',
        'p2j-progress', 'p2j-progress-bar', 'p2j-progress-text', 'p2j-result', 'p2j-result-txt', 'p2j-share',
        'p2j-folder', 'p2j-src-name', 'p2j-src-sub', 'p2j-stage-hint', 'p2j-view', 'p2j-thumbs', 'p2j-sheet-wrap',
        'p2j-sheet', 'p2j-restore',
    ]) E[id.replace(/^p2j-/, '').replace(/-([a-z])/g, (_, c) => c.toUpperCase())] = $(id);
    E.goLabel = E.go && E.go.querySelector('.p2j-go-label');
}

const fmtVal = () => (E.format && E.format.value) || 'jpeg';
const dpiVal = () => Number(E.dpi && E.dpi.value) || 96;
const qualityVal = () => (Number(E.quality && E.quality.value) || 92) / 100;
const dpiChoices = () => Array.from(E.dpi ? E.dpi.options : []).map((o) => Number(o.value)).filter(Boolean);
const extOf = (f) => (f === 'jpeg' ? 'jpg' : f);
const mimeOf = (f) => (f === 'jpeg' ? 'image/jpeg' : f === 'png' ? 'image/png' : 'image/webp');
const outBase = () => ((E.outputName && E.outputName.value) || '').trim() || baseName(S.name);

/* ── page list ⇄ range text: formatRange / parseRange live in sheet.js ──── */
const exportOrder = () => (S.mode === 'sheet' ? S.order.slice() : S.order.slice().sort((a, b) => a - b));

function setOrder(order, from) {
    S.order = order;
    if (from !== 'range') {
        E.range.value = order.length === S.sizes.length && order.every((v, i) => v === i) ? '1-' + S.sizes.length : formatRange(order);
        E.range.classList.remove('is-bad');
        if (E.rangeErr) E.rangeErr.hidden = true;
    }
    syncThumbs();
    invalidate();
    refresh();
}

/* ── loading ──────────────────────────────────────────────────────────────── */
async function onFile(file) {
    if (Array.isArray(file)) file = file[0];
    if (!file || !file.size) return;
    const seq = ++S.loadSeq;
    closeDoc();
    S.file = file;
    S.name = file.name || 'document.pdf';
    E.srcName.textContent = S.name;
    E.srcSub.textContent = humanSize(file.size);
    E.outputName.placeholder = baseName(S.name);
    E.workspace.hidden = false;
    // Until pdf.js has read the file there is nothing to show — say so, and
    // keep the button from promising work it cannot start yet. On a slow phone
    // or a busy machine the engine takes seconds to boot (measured 20 s+ on a
    // loaded laptop), and an empty panel reads as "broken".
    E.stageHint.textContent = t('opening');
    E.preview.textContent = '';
    E.read.hidden = true;
    E.warn.hidden = true;
    E.go.disabled = true;
    E.view.classList.add('is-loading');
    stage('loading');
    try {
        const bytes = (window.FileBytes && typeof window.FileBytes.arrayBuffer === 'function')
            ? await window.FileBytes.arrayBuffer(file) : await file.arrayBuffer();
        const pdfjs = await Pages.loadPdfjs();
        const doc = await Pages.openDocument(pdfjs, bytes);
        if (seq !== S.loadSeq) { try { doc.destroy(); } catch (_) {} return; }
        const sizes = await Pages.pageSizes(doc);
        if (seq !== S.loadSeq) { try { doc.destroy(); } catch (_) {} return; }
        S.doc = doc;
        S.sizes = sizes;
        S.ink = new Map();
        S.textPt = undefined;
        S.order = sizes.map((s) => s.index);
        E.range.value = '1-' + sizes.length;
        E.srcSub.textContent = (sizes.length === 1 ? t('file_pages_one') : t('file_pages', { count: sizes.length })) + ' · ' + humanSize(file.size);
        E.view.classList.remove('is-loading');
        buildThumbs();
        refresh();
        measureText(seq);
        if (S.mode === 'sheet' && S.trim) findInk();
        ae('file_loaded', { size: file.size, pages: sizes.length });
        stage('ready');
    } catch (err) {
        if (seq !== S.loadSeq) return;
        const m = String((err && (err.name + ' ' + err.message)) || err);
        E.view.classList.remove('is-loading');
        E.workspace.hidden = true;
        if (/password/i.test(m)) { toast(t('err_encrypted'), 'error'); ae('load_failed', { kind: 'password' }); }
        else if (/pdfjs_load_fail|dynamically imported|import/i.test(m)) { toast(t('err_engine'), 'error'); ae('load_failed', { kind: 'engine' }); }
        else { toast(t('err_corrupted'), 'error'); ae('load_failed', { kind: 'corrupt', msg: m.slice(0, 120) }); }
    }
}

function closeDoc() {
    S.gen++;
    cancelRun();
    invalidate();
    if (S.doc) { try { S.doc.destroy(); } catch (_) {} }
    S.doc = null;
    S.sizes = [];
    S.order = [];
    S.plan = null;
    S.pending = null;
    E.thumbs.innerHTML = '';
    E.sheet.innerHTML = '';
    previewCache.clear();
    if (E.folder) E.folder.hidden = true;
}

/* ── how big is the running text? ─────────────────────────────────────────── */
async function measureText(seq) {
    const n = S.sizes.length;
    const pick = [...new Set([0, Math.floor((n - 1) / 2), n - 1])].filter((i) => i >= 0);
    try {
        const pt = await Pages.bodyTextPt(S.doc, pick);
        if (seq !== S.loadSeq) return;
        S.textPt = pt;
    } catch (_) { S.textPt = null; }
    refresh();
}

/* ── margins (only when asked for) ────────────────────────────────────────── */
async function findInk() {
    if (!S.doc) return;
    const run = ++S.inkRun;
    const todo = S.order.filter((i) => !S.ink.has(i));
    if (!todo.length) return;
    let done = 0;
    for (const i of todo) {
        if (run !== S.inkRun || !S.doc) return;
        if (!S.ink.has(i)) {
            try { S.ink.set(i, await Pages.inkBoxPt(S.doc, i, S.sizes[i])); }
            catch (_) { S.ink.set(i, undefined); }
        }
        done++;
        if (E.stageHint && S.mode === 'sheet') E.stageHint.textContent = t('trim_working', { i: done, n: todo.length });
    }
    // One redraw at the end: the crop of a column is the union over ALL pages,
    // so every partial answer would move every page and re-render them all.
    if (run === S.inkRun) refresh();
}
function inkReady(indices) { return indices.every((i) => S.ink.has(i)); }

/* ── plan ─────────────────────────────────────────────────────────────────── */
function planFor(order, overrides) {
    const pages = order.map((i) => ({
        index: i, w: S.sizes[i].w, h: S.sizes[i].h,
        ink: S.trim ? (S.ink.has(i) ? S.ink.get(i) : undefined) : undefined,
    }));
    return planSheet(pages, Object.assign({
        dpi: dpiVal(), layout: S.layout, columns: S.columns, gapPt: S.gapPt, trim: S.trim,
    }, overrides || {}));
}
function currentChecks() {
    const plan = S.plan;
    const fmt = fmtVal();
    return checkPlan(plan, fmt, fmt === 'webp' ? canvasLimits() : null);
}

/* ── rendering the page ───────────────────────────────────────────────────── */
function refresh() {
    if (!S.doc) return;
    const n = S.order.length;
    const total = S.sizes.length;

    // Mode cards.
    E.modePagesSub.textContent = t('mode_pages_sub');
    const single = total < 2;
    E.modeSheet.disabled = single;
    E.modeSheetSub.textContent = single ? t('mode_sheet_single') : t('mode_sheet_sub');
    if (single && S.mode === 'sheet') S.mode = 'pages';
    for (const b of [E.modePages, E.modeSheet]) {
        const on = b.dataset.mode === S.mode;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.classList.toggle('on', on);
        b.tabIndex = on ? 0 : -1;
    }
    E.sheetOpts.hidden = S.mode !== 'sheet';
    E.colsF.hidden = S.layout !== 'grid';
    for (const b of E.layout.querySelectorAll('[data-layout]')) {
        const on = b.dataset.layout === S.layout;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    }
    for (const b of E.cols.querySelectorAll('[data-cols]')) b.classList.toggle('on', Number(b.dataset.cols) === S.columns);
    E.gap.value = String(S.gapPt);
    E.gapVal.textContent = S.gapPt ? t('gap_mm', { mm: (S.gapPt * 25.4 / 72).toFixed(1).replace(/\.0$/, '') }) : t('gap_none');
    for (const b of E.bg.querySelectorAll('[data-bg]')) b.setAttribute('aria-pressed', b.dataset.bg === S.bg ? 'true' : 'false');
    E.bgCustom.value = S.bg;
    E.trim.checked = S.trim;
    E.qualityRow.hidden = fmtVal() === 'png';
    E.qualityVal.textContent = Math.round(qualityVal() * 100) + '%';
    E.moreSub.textContent = n === total ? t('pages_all', { count: total }) : t('pages_some', { n, total });

    // Stage.
    E.thumbs.hidden = S.mode !== 'pages';
    E.sheetWrap.hidden = S.mode !== 'sheet';
    E.restore.hidden = !(S.mode === 'sheet' && n < total);
    if (S.mode === 'sheet') {
        S.plan = planFor(S.order);
        buildSheet();
        if (!(S.trim && !inkReady(S.order))) E.stageHint.textContent = n > 1 ? t('order_hint') : '';
    } else {
        S.plan = null;
        E.stageHint.textContent = total > 1 ? t('thumbs_hint') : '';
    }
    summarize();
}

function summarize() {
    const n = S.order.length;
    const warn = E.warn;
    warn.hidden = true;
    warn.innerHTML = '';
    let blocked = n === 0;

    if (S.mode === 'pages') {
        const tpl = n === 1 ? t('preview_count_one', { count: '<b>1</b>' }) : t('preview_count', { count: '<b>' + n + '</b>' });
        E.preview.innerHTML = tpl;
        // Where the contract's reader clicked four times: say, right here, that
        // one image is possible.
        if (n > 1 && S.sizes.length > 1) {
            const nudge = document.createElement('button');
            nudge.type = 'button';
            nudge.className = 'p2j-link p2j-nudge';
            nudge.textContent = t('sheet_nudge');
            nudge.addEventListener('click', () => { setMode('sheet', 'nudge'); });
            E.preview.append(' ', nudge);
        }
    } else {
        const plan = S.plan;
        const chk = currentChecks();
        E.preview.innerHTML = '';
        const b = document.createElement('b');
        b.textContent = t('sheet_dims', { w: fmtInt(plan.width), h: fmtInt(plan.height) });
        E.preview.append(b);
        if (plan.skipped.length) E.preview.append(' · ' + t('blank_skipped', { n: plan.skipped.length }));
        if (!chk.ok) {
            blocked = true;
            showWarning(plan, chk);
        }
    }

    // Readability — "DPI" means nothing to most people; letter size does.
    if (S.textPt) {
        const em = S.textPt * dpiVal() / 72;
        const tip = em < 12 ? t('read_small', { dpi: 150 }) : em < 18 ? t('read_ok') : t('read_sharp');
        E.read.textContent = tip;
        E.read.className = 'p2j-read ' + (em < 12 ? 'is-small' : em < 18 ? 'is-ok' : 'is-sharp');
        E.read.hidden = false;
    } else {
        E.read.hidden = true;
    }

    if (!S.busy && !S.result) E.go.disabled = blocked;
}

function showWarning(plan, chk) {
    const warn = E.warn;
    const fmt = fmtVal();
    let msg;
    if (chk.reason === 'empty') msg = t('warn_empty');
    else if (chk.reason === 'side' && fmt === 'webp') msg = t('warn_webp_side', { limit: fmtInt(chk.limit) });
    else if (chk.reason === 'side') msg = t('warn_too_tall', { w: fmtInt(plan.width), h: fmtInt(plan.height), limit: fmtInt(chk.limit) });
    else if (chk.reason === 'canvas') msg = t('warn_canvas');
    else msg = t('warn_budget', { mp: fmtInt(chk.mp) });
    const p = document.createElement('p');
    p.textContent = msg;
    warn.append(p);

    const acts = document.createElement('div');
    acts.className = 'tc-actions';
    const fix = (label, kind, apply) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tc-btn sm';
        b.textContent = label;
        b.addEventListener('click', () => { ae('sheet_fix', { kind, reason: chk.reason }); apply(); });
        acts.append(b);
    };
    if (chk.reason !== 'empty') {
        if (fmt === 'webp') fix(t('fix_jpg'), 'jpg', () => { E.format.value = 'jpeg'; onSetting('format'); });
        const pages = S.order.map((i) => ({ index: i, w: S.sizes[i].w, h: S.sizes[i].h, ink: S.trim ? S.ink.get(i) : undefined }));
        const s = suggestFit(pages, { dpi: dpiVal(), layout: S.layout, columns: S.columns, gapPt: S.gapPt, trim: S.trim, dpiChoices: dpiChoices() },
            fmt, fmt === 'webp' ? canvasLimits() : null);
        if (s && s.dpi) fix(t('fix_dpi', { dpi: s.dpi }), 'dpi', () => { E.dpi.value = String(s.dpi); onSetting('dpi'); });
        if (s && s.columns) fix(t('fix_cols', { n: s.columns }), 'cols', () => { S.layout = 'grid'; S.columns = s.columns; saveSetting('layout', 'grid'); saveSetting('columns', s.columns); invalidate(); refresh(); });
    }
    if (acts.childElementCount) warn.append(acts);
    warn.hidden = false;
    if (S.lastWarn !== chk.reason + plan.height) {
        S.lastWarn = chk.reason + plan.height;
        ae('sheet_warn', { reason: chk.reason, w: plan.width, h: plan.height, dpi: dpiVal(), fmt });
    }
}

/* ── thumbnails (pages mode) ──────────────────────────────────────────────── */
let thumbObs = null;
const queue = [];
let running = 0;
function enqueue(job) { queue.push(job); pump(); }
function pump() {
    while (running < 2 && queue.length) {
        const job = queue.shift();
        running++;
        Promise.resolve().then(job).catch(() => {}).finally(() => { running--; pump(); });
    }
}

function buildThumbs() {
    E.thumbs.innerHTML = '';
    if (thumbObs) thumbObs.disconnect();
    thumbObs = ('IntersectionObserver' in window)
        ? new IntersectionObserver(onThumbSeen, { root: E.view, rootMargin: '400px 0px' }) : null;
    const gen = S.gen;
    for (const s of S.sizes) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'p2j-thumb';
        b.dataset.i = String(s.index);
        b.setAttribute('aria-label', t('page_n', { n: s.index + 1 }));
        const num = document.createElement('span');
        num.className = 'p2j-thumb-num';
        num.textContent = String(s.index + 1);
        b.append(num);
        E.thumbs.append(b);
        if (thumbObs) thumbObs.observe(b); else enqueue(() => renderThumb(b, gen));
    }
    syncThumbs();
}
function onThumbSeen(entries) {
    const gen = S.gen;
    for (const e of entries) {
        if (!e.isIntersecting) continue;
        thumbObs.unobserve(e.target);
        enqueue(() => renderThumb(e.target, gen));
    }
}
async function renderThumb(btn, gen) {
    if (gen !== S.gen || !S.doc) return;
    const i = Number(btn.dataset.i);
    const s = S.sizes[i];
    const box = 104, boxH = 146;
    const f = Math.min(box / s.w, boxH / s.h);
    const canvas = await Pages.renderPreview(S.doc, i, { x: 0, y: 0, w: s.w, h: s.h },
        Math.max(1, Math.round(s.w * f)), Math.max(1, Math.round(s.h * f)), Math.min(2, window.devicePixelRatio || 1));
    if (gen !== S.gen) return;
    btn.prepend(canvas);
}
function syncThumbs() {
    const sel = new Set(S.order);
    for (const b of E.thumbs.querySelectorAll('.p2j-thumb')) {
        b.setAttribute('aria-pressed', sel.has(Number(b.dataset.i)) ? 'true' : 'false');
    }
}

/* ── the single-image preview (sheet mode) ────────────────────────────────── */
/* Drawn from THE plan — the same numbers the export uses — as absolutely
   positioned page tiles over the sheet's background. Not one big canvas: a
   preview of 14 pages is a 7 000 CSS px tall column, and a canvas that tall is
   blank on a phone (the compositor cannot upload it as a texture). */
const previewCache = new Map();
let sheetObs = null;

function previewScale(plan) {
    const avail = Math.max(120, (E.view.clientWidth || 600) - 24);
    const target = plan.layout === 'column' ? Math.min(avail, 460) : avail;
    return Math.min(1, target / Math.max(1, plan.width));
}

function buildSheet() {
    const plan = S.plan;
    const s = previewScale(plan);
    const sheet = E.sheet;
    sheet.style.width = Math.max(1, Math.round(plan.width * s)) + 'px';
    sheet.style.height = Math.max(1, Math.round(plan.height * s)) + 'px';
    sheet.style.background = S.bg;
    if (sheetObs) sheetObs.disconnect();
    sheetObs = ('IntersectionObserver' in window)
        ? new IntersectionObserver(onTileSeen, { root: E.view, rootMargin: '600px 0px' }) : null;

    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const n = plan.placements.length;
    const frag = document.createDocumentFragment();
    const arrows = plan.layout === 'column' ? ['▲', '▼'] : ['◀', '▶'];
    for (const p of plan.placements) {
        const tile = document.createElement('div');
        tile.className = 'p2j-sp';
        tile.dataset.i = String(p.index);
        tile.dataset.n = String(p.n);
        const cssW = Math.max(1, Math.round(p.w * s)), cssH = Math.max(1, Math.round(p.h * s));
        tile.style.left = Math.round(p.x * s) + 'px';
        tile.style.top = Math.round(p.y * s) + 'px';
        tile.style.width = cssW + 'px';
        tile.style.height = cssH + 'px';
        const key = p.index + '|' + [p.crop.x, p.crop.y, p.crop.w, p.crop.h].map((v) => Math.round(v * 10)).join(',') + '|' + Math.round(cssW * dpr);
        tile.dataset.key = key;
        tile.dataset.cw = String(cssW);
        tile.dataset.ch = String(cssH);
        const cached = previewCache.get(key);
        if (cached) {
            cached.style.width = cssW + 'px';
            cached.style.height = cssH + 'px';
            tile.append(cached);
        } else if (sheetObs) sheetObs.observe(tile);

        const num = document.createElement('span');
        num.className = 'p2j-thumb-num';
        num.textContent = String(p.index + 1);
        tile.append(num);

        if (n > 1) {
            const tools = document.createElement('div');
            tools.className = 'p2j-sp-tools';
            const mk = (act, glyph, label, disabled) => {
                const b = document.createElement('button');
                b.type = 'button';
                b.dataset.act = act;
                b.textContent = glyph;
                b.setAttribute('aria-label', label);
                b.title = label;
                b.disabled = !!disabled;
                tools.append(b);
            };
            mk('up', arrows[0], t('move_earlier', { n: p.index + 1 }), p.n === 0);
            mk('down', arrows[1], t('move_later', { n: p.index + 1 }), p.n === n - 1);
            mk('rm', '✕', t('remove_page', { n: p.index + 1 }), false);
            tile.append(tools);
        }
        frag.append(tile);
    }
    sheet.replaceChildren(frag);
    if (!sheetObs) for (const tile of sheet.children) enqueue(() => renderTile(tile, S.gen));
}
function onTileSeen(entries) {
    const gen = S.gen;
    for (const e of entries) {
        if (!e.isIntersecting) continue;
        sheetObs.unobserve(e.target);
        enqueue(() => renderTile(e.target, gen));
    }
}
async function renderTile(tile, gen) {
    if (gen !== S.gen || !S.doc || !tile.isConnected) return;
    const key = tile.dataset.key;
    if (previewCache.has(key)) return;
    const p = S.plan && S.plan.placements.find((q) => String(q.index) === tile.dataset.i);
    if (!p) return;
    const cssW = Number(tile.dataset.cw), cssH = Number(tile.dataset.ch);
    const canvas = await Pages.renderPreview(S.doc, p.index, p.crop, cssW, cssH, Math.min(2, window.devicePixelRatio || 1));
    if (gen !== S.gen) return;
    if (previewCache.size > 160) previewCache.delete(previewCache.keys().next().value);
    previewCache.set(key, canvas);
    if (tile.isConnected && tile.dataset.key === key) tile.prepend(canvas);
}

function onSheetClick(e) {
    const btn = e.target.closest('button[data-act]');
    if (!btn || S.busy) return;
    const tile = btn.closest('.p2j-sp');
    const n = Number(tile.dataset.n);
    const order = S.order.slice();
    // The plan skips blank pages when trimming, so its position n indexes the
    // PLACED pages; map it back to the page and move that page in the list.
    const index = Number(tile.dataset.i);
    const at = order.indexOf(index);
    if (at < 0) return;
    const act = btn.dataset.act;
    if (act === 'rm') {
        if (order.length <= 1) return;
        order.splice(at, 1);
        ae('page_remove', { n: order.length });
    } else {
        const placed = S.plan.placements.map((q) => q.index);
        const other = placed[act === 'up' ? n - 1 : n + 1];
        if (other == null) return;
        const bt = order.indexOf(other);
        order[at] = other;
        order[bt] = index;
        ae('page_move', { dir: act });
    }
    setOrder(order);
    // Keep the keyboard where the person is: on the same control of the page
    // that just moved (or on the next page after a removal).
    const focusIndex = act === 'rm' ? S.order[Math.min(at, S.order.length - 1)] : index;
    const again = E.sheet.querySelector('.p2j-sp[data-i="' + focusIndex + '"] button[data-act="' + (act === 'rm' ? 'rm' : act) + '"]')
        || E.sheet.querySelector('.p2j-sp[data-i="' + focusIndex + '"] button');
    if (again && !again.disabled) again.focus({ preventScroll: true });
}

/* ── settings ─────────────────────────────────────────────────────────────── */
function setMode(mode, via) {
    if (mode === S.mode || (mode === 'sheet' && S.sizes.length < 2)) return;
    S.mode = mode;
    saveSetting('mode', mode);
    ae('mode', { to: mode, via: via || 'card', pages: S.order.length });
    invalidate();
    refresh();
    if (mode === 'sheet' && S.trim) findInk();
}
function onSetting(which) {
    if (which === 'format') saveSetting('format', fmtVal());
    if (which === 'dpi') saveSetting('dpi', String(dpiVal()));
    if (which === 'quality') saveSetting('quality', String(Math.round(qualityVal() * 100)));
    invalidate();
    refresh();
}

/* ── export ───────────────────────────────────────────────────────────────── */
function setBusy(on) {
    S.busy = on;
    E.go.classList.toggle('is-busy', on);
    E.go.classList.remove('is-done', 'is-err');
    E.goLabel.textContent = on ? t('cancel') : t('convert_btn');
    E.go.disabled = false;
    E.progress.classList.toggle('is-on', on);
    if (!on) { E.progressBar.style.width = '0%'; E.progressText.textContent = ''; }
    for (const el of [E.modePages, E.modeSheet]) el.disabled = on || (el === E.modeSheet && S.sizes.length < 2);
}
function progress(frac, text) {
    E.progressBar.style.width = Math.max(0, Math.min(100, frac * 100)).toFixed(1) + '%';
    if (text != null) E.progressText.textContent = text;
}
function invalidate() {
    if (S.result) {
        S.result = null;
        E.result.hidden = true;
        E.go.classList.remove('is-done', 'is-err');
        E.goLabel.textContent = t('convert_btn');
    }
}
function cancelRun() { if (S.abort) { try { S.abort.abort(); } catch (_) {} } }

async function onGo() {
    if (S.busy) { cancelRun(); return; }
    if (S.result) {
        window.__downloadFile(S.result.blob, S.result.name);
        ae('download_again', { mode: S.mode });
        return;
    }
    if (!S.doc) { toast(t('err_no_file'), 'error'); return; }
    const order = exportOrder();
    if (!order.length) { toast(t('err_bad_range'), 'error'); return; }
    if (S.mode === 'sheet') {
        const chk = currentChecks();
        if (!chk.ok) { summarize(); toast(E.warn.querySelector('p') ? E.warn.querySelector('p').textContent : t('err_fatal'), 'error'); return; }
    }

    if (window.__toolLimit) {
        S.token = window.__toolLimit.begin(SLUG, window.__toolLimit.fileKey(S.file));
        if (!S.token) return;   // cap spent — begin() already said so where it is seen
    }
    const ac = new AbortController();
    S.abort = ac;
    setBusy(true);
    const t0 = performance.now();
    const fmt = fmtVal();
    ae('convert_start', {
        mode: S.mode, count: order.length, fmt, dpi: dpiVal(),
        layout: S.mode === 'sheet' ? S.layout : undefined,
        cols: S.mode === 'sheet' && S.layout === 'grid' ? S.columns : undefined,
        gap: S.mode === 'sheet' ? S.gapPt : undefined,
        trim: S.mode === 'sheet' ? (S.trim ? 1 : 0) : undefined,
    });
    stage('processing');
    try {
        const out = S.mode === 'sheet' ? await runSheet(order, ac.signal) : await runPages(order, ac.signal);
        if (!out) { finishFail(); return; }         // handed to the folder lane or refused
        S.result = out;
        window.__downloadFile(out.blob, out.name);
        showResult(out);
        if (S.token) { S.token.commit(); S.token = null; }
        const ms = Math.round(performance.now() - t0);
        ae('convert_done', { mode: S.mode, count: order.length, bytes: out.blob.size, w: out.w, h: out.h, ms, fmt, via: out.via });
        if (typeof window.__aeToolComplete === 'function') {
            window.__aeToolComplete({ pages: S.sizes.length, bytes: out.blob.size, mode: S.mode });
        }
        stage('complete');
    } catch (err) {
        // A new file picked mid-export destroys the document under the run; the
        // error that surfaces then is pdf.js's, but the cause is the abort.
        const aborted = ac.signal.aborted || (err && (err.name === 'AbortError' || err.code === 'ABORTED'));
        if (S.token) { S.token.abort(); S.token = null; }
        setBusy(false);
        if (aborted) { ae('cancelled', { mode: S.mode }); stage('ready'); summarize(); return; }
        const msg = err && err.code === 'WEBP_PAGE_TOO_BIG' ? t('err_webp_page_too_big') : t('err_fatal');
        toast(msg, 'error');
        E.go.classList.add('is-err');
        E.goLabel.textContent = t('retry');
        try { if (window.__aeCritical) window.__aeCritical('tool_error', { slug: SLUG, klass: S.mode, code: err && err.code, msg: String((err && err.message) || err).slice(0, 300) }); } catch (_) {}
        stage('error');
        return;
    } finally {
        S.abort = null;
    }
}
function finishFail() {
    if (S.token) { S.token.abort(); S.token = null; }
    setBusy(false);
    summarize();
}

/* One image per page. A page that fits a canvas goes through the browser's
   own encoder (fast); a page bigger than this device's canvas — 600 DPI on an
   iPhone — goes through the same band pipeline as the long image, because a
   canvas past its limit is silently blank. */
async function runPages(order, signal) {
    const fmt = fmtVal();
    const k = dpiVal() / 72;
    const q = qualityVal();
    const base = outBase();
    const items = [];
    const lim = canvasLimits();
    for (let j = 0; j < order.length; j++) {
        if (signal.aborted) throw coded('aborted', 'ABORTED');
        const i = order[j];
        progress(j / order.length * 0.9, t('stage_render') + ' · ' + (j + 1) + '/' + order.length);
        const s = S.sizes[i];
        const w = Math.max(1, Math.round(s.w * k)), h = Math.max(1, Math.round(s.h * k));
        let blob;
        if (canvasFits(w, h, lim)) {
            const page = await S.doc.getPage(i + 1);
            const canvas = await Pages.renderRegion(page, w, h, k, 0, 0);
            blob = await toBlob(canvas, mimeOf(fmt), q);
            canvas.width = canvas.height = 0;
        } else if (fmt !== 'webp') {
            const plan = { width: w, height: h, k, placements: [{ n: 0, index: i, x: 0, y: 0, w, h, crop: { x: 0, y: 0, w: s.w, h: s.h } }] };
            blob = await encodePlan(plan, fmt, q, signal, null);
        } else {
            throw coded('page too big for webp', 'WEBP_PAGE_TOO_BIG');
        }
        items.push({ name: base + '-page-' + (i + 1) + '.' + extOf(fmt), blob });
        await breathe();
    }
    if (items.length === 1) return { blob: items[0].blob, name: items[0].name, via: 'single' };
    return deliverMany(items, base);
}

/* All pages, one image. */
async function runSheet(order, signal) {
    const fmt = fmtVal();
    const q = qualityVal();
    if (S.trim && !inkReady(order)) {
        progress(0.02, t('stage_margins'));
        await findInk();
        if (signal.aborted) throw coded('aborted', 'ABORTED');
    }
    const plan = planFor(order);
    S.plan = plan;
    const chk = checkPlan(plan, fmt, fmt === 'webp' ? canvasLimits() : null);
    if (!chk.ok) { refresh(); return null; }
    const blob = await encodePlan(plan, fmt, q, signal, (f) => progress(0.05 + f * 0.95, t('stage_encode') + ' · ' + Math.round(f * 100) + '%'));
    return { blob, name: outBase() + '.' + extOf(fmt), w: plan.width, h: plan.height, via: 'sheet' };
}

function pageBudget() {
    const gb = Number(navigator.deviceMemory) || 4;
    return Math.min(384, Math.max(96, gb * 32)) * 1024 * 1024;
}

/* plan → file, band by band. */
async function encodePlan(plan, fmt, quality, signal, onProgress) {
    const src = Pages.pageSource(S.doc, plan, { budgetBytes: pageBudget() });
    const bands = composeBands(plan, src, { bandRows: 256, background: parseHex(S.bg), signal });
    const W = plan.width, H = plan.height;
    let rowsDone = 0;
    async function* paced() {
        for await (const b of bands) {
            yield b;
            rowsDone += b.rows;
            if (onProgress) onProgress(rowsDone / H);
            await breathe();       // input and paint get their turn between bands
        }
    }
    try {
        if (fmt === 'jpeg') {
            const enc = jpegEncoder({ width: W, height: H, quality, density: Math.round(plan.k * 72) });
            for await (const b of paced()) enc.push(b.data, b.rows);
            return enc.finish();
        }
        if (fmt === 'png') {
            return await PngStream.encode({
                width: W, height: H, alpha: false,
                rows: (async function* () {
                    for await (const b of paced()) {
                        for (let r = 0; r < b.rows; r++) yield b.data.subarray(r * W * 4, (r + 1) * W * 4);
                    }
                })(),
            });
        }
        // WebP has no streaming encoder in the browser: one canvas, inside the
        // limit checkPlan() already verified against canvasLimits().
        const canvas = document.createElement('canvas');
        canvas.width = W; canvas.height = H;
        const ctx = canvas.getContext('2d');
        for await (const b of paced()) {
            ctx.putImageData(new ImageData(b.data.slice(0, W * b.rows * 4), W, b.rows), 0, b.y0);
        }
        const blob = await toBlob(canvas, 'image/webp', quality);
        canvas.width = canvas.height = 0;
        return blob;
    } finally {
        src.dispose();
    }
}

function toBlob(canvas, mime, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((b) => {
            // null or empty = the canvas never filled; a download of it would be
            // a blank picture, so it is an error, not a file.
            if (!b || !b.size) { reject(coded('encode produced nothing', 'ENCODE_EMPTY')); return; }
            resolve(b);
        }, mime, mime === 'image/png' ? undefined : quality);
    });
}

/* Several files. An archive by default; a folder when the archive would not
   fit the browser's live-blob budget (see batch-deliver.js — past it the
   archive "downloads" and nothing lands on disk). The folder picker needs a
   fresh click, so the pages are kept and a button is offered. */
async function deliverMany(items, base) {
    const BD = window.__batchDeliver;
    const total = items.reduce((a, i) => a + i.blob.size, 0);
    if (BD && BD.fitsArchive && !BD.fitsArchive(total)) {
        S.pending = items;
        if (BD.folderAvailable && BD.folderAvailable()) {
            E.folder.hidden = false;
            toast(t('need_folder'), 'warning');
            ae('zip_too_big', { bytes: total, folder: 1 });
            return null;
        }
        ae('zip_too_big', { bytes: total, folder: 0 });
        throw coded('archive too big', 'ZIP_TOO_BIG');
    }
    if (!BD || typeof BD.packZip !== 'function') throw coded('batch deliver missing', 'SCRIPT_LOAD_FAILED');
    progress(0.93, t('stage_zipping'));
    const zip = await BD.packZip(items);
    return { blob: zip, name: base + '.zip', via: 'zip', files: items.length };
}

async function saveToFolder() {
    const BD = window.__batchDeliver;
    const items = S.pending;
    if (!items || !BD) return;
    try {
        const sink = await BD.pickFolder();
        if (!sink) return;
        let done = 0;
        for (const it of items) {
            await sink.write(it.name, it.blob);
            done++;
            progress(done / items.length, t('saving_n', { i: done, n: items.length }));
        }
        await sink.close();
        S.pending = null;
        E.folder.hidden = true;
        E.resultTxt.textContent = t('saved_folder', { n: items.length });
        E.result.hidden = false;
        E.share.hidden = true;
        ae('folder_saved', { n: items.length });
    } catch (err) {
        if (!/abort/i.test(String(err && err.name))) toast(t('err_folder'), 'error');
    } finally {
        E.progress.classList.remove('is-on');
    }
}

function showResult(out) {
    setBusy(false);
    E.go.classList.add('is-done');
    E.goLabel.textContent = t('saved');
    const line = out.w
        ? t('result_sheet', { name: out.name, w: fmtInt(out.w), h: fmtInt(out.h), size: humanSize(out.blob.size) })
        : t('result_file', { name: out.name, size: humanSize(out.blob.size) });
    E.resultTxt.textContent = line;
    E.result.hidden = false;
    E.share.hidden = !canShare(out);
}

function shareFile(out) {
    try { return new File([out.blob], out.name, { type: out.blob.type || mimeOf(fmtVal()) }); }
    catch (_) { return null; }
}
function canShare(out) {
    try {
        if (!navigator.share || !navigator.canShare) return false;
        const f = shareFile(out);
        return !!(f && navigator.canShare({ files: [f] }));
    } catch (_) { return false; }
}
async function onShare() {
    const out = S.result;
    if (!out) return;
    const f = shareFile(out);
    try {
        await navigator.share({ files: [f] });
        ae('share', { ok: 1, mode: S.mode });
    } catch (err) {
        if (!/abort/i.test(String(err && err.name))) { ae('share', { ok: 0 }); toast(t('err_share'), 'error'); }
    }
}

/* ── wiring ───────────────────────────────────────────────────────────────── */
function radioKeys(group, selector, pick) {
    group.addEventListener('keydown', (e) => {
        if (!/^Arrow(Left|Right|Up|Down)$/.test(e.key)) return;
        const items = Array.from(group.querySelectorAll(selector)).filter((b) => !b.disabled);
        const at = items.indexOf(document.activeElement);
        if (at < 0) return;
        e.preventDefault();
        const dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
        const next = items[(at + dir + items.length) % items.length];
        next.focus();
        pick(next);
    });
}

function init() {
    grab();
    if (!E.upload || !E.go) return;

    // Restore stored preferences onto the controls before anything reads them.
    const fmt0 = loadSetting('format', '');
    if (fmt0 && E.format.querySelector('option[value="' + fmt0 + '"]')) E.format.value = fmt0;
    const dpi0 = String(loadSetting('dpi', ''));
    if (dpi0 && E.dpi.querySelector('option[value="' + dpi0 + '"]')) E.dpi.value = dpi0;
    const q0 = Number(loadSetting('quality', 0));
    if (q0 >= 40 && q0 <= 100) E.quality.value = String(q0);

    const onFileCb = (f) => { onFile(f); };
    if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(E.upload, onFileCb);
    else E.upload.addEventListener('tool-uploader:file', (e) => {
        const d = e.detail || {};
        onFileCb(d.file || (d.files && d.files[0]));
    });
    E.upload.addEventListener('tool-uploader:clear', () => {
        S.loadSeq++;
        closeDoc();
        E.workspace.hidden = true;
    });

    for (const b of [E.modePages, E.modeSheet]) b.addEventListener('click', () => setMode(b.dataset.mode, 'card'));
    radioKeys(E.modePages.parentElement, '[data-mode]', (b) => setMode(b.dataset.mode, 'key'));

    E.layout.addEventListener('click', (e) => {
        const b = e.target.closest('[data-layout]');
        if (!b || b.dataset.layout === S.layout) return;
        S.layout = b.dataset.layout;
        saveSetting('layout', S.layout);
        ae('sheet_opt', { k: 'layout', v: S.layout });
        invalidate(); refresh();
    });
    radioKeys(E.layout, '[data-layout]', (b) => b.click());
    E.cols.addEventListener('click', (e) => {
        const b = e.target.closest('[data-cols]');
        if (!b) return;
        S.columns = Number(b.dataset.cols);
        saveSetting('columns', S.columns);
        ae('sheet_opt', { k: 'cols', v: S.columns });
        invalidate(); refresh();
    });
    let gapRaf = 0;
    E.gap.addEventListener('input', () => {
        S.gapPt = Number(E.gap.value) || 0;
        if (gapRaf) return;
        gapRaf = requestAnimationFrame(() => { gapRaf = 0; invalidate(); refresh(); });
    });
    E.gap.addEventListener('change', () => { saveSetting('gap', S.gapPt); ae('sheet_opt', { k: 'gap', v: S.gapPt }); });
    E.bg.addEventListener('click', (e) => {
        const b = e.target.closest('[data-bg]');
        if (!b) return;
        S.bg = b.dataset.bg;
        saveSetting('bg', S.bg);
        ae('sheet_opt', { k: 'bg', v: S.bg });
        invalidate(); refresh();
    });
    E.bgCustom.addEventListener('input', () => {
        if (!/^#[0-9a-f]{6}$/i.test(E.bgCustom.value)) return;
        S.bg = E.bgCustom.value.toLowerCase();
        invalidate(); refresh();
    });
    E.bgCustom.addEventListener('change', () => { saveSetting('bg', S.bg); ae('sheet_opt', { k: 'bg', v: 'custom' }); });
    E.trim.addEventListener('change', () => {
        S.trim = E.trim.checked;
        saveSetting('trim', S.trim ? '1' : '0');
        ae('sheet_opt', { k: 'trim', v: S.trim ? 1 : 0 });
        invalidate(); refresh();
        if (S.trim) findInk();
    });

    E.format.addEventListener('change', () => onSetting('format'));
    E.dpi.addEventListener('change', () => onSetting('dpi'));
    E.quality.addEventListener('input', () => { E.qualityVal.textContent = E.quality.value + '%'; invalidate(); });
    E.quality.addEventListener('change', () => onSetting('quality'));
    E.outputName.addEventListener('input', invalidate);
    E.range.addEventListener('input', () => {
        if (!S.sizes.length) return;
        const order = parseRange(E.range.value, S.sizes.length);
        const bad = !order;
        E.range.classList.toggle('is-bad', bad);
        if (E.rangeErr) E.rangeErr.hidden = !bad;
        if (!bad) {
            setOrder(order, 'range');
            if (S.mode === 'sheet' && S.trim) findInk();
        }
    });

    E.thumbs.addEventListener('click', (e) => {
        const b = e.target.closest('.p2j-thumb');
        if (!b || S.busy) return;
        const i = Number(b.dataset.i);
        let order = S.order.slice();
        if (order.includes(i)) {
            if (order.length <= 1) return;
            order = order.filter((v) => v !== i);
        } else {
            order.push(i);
            order.sort((a, c) => a - c);
        }
        ae('thumb_toggle', { on: order.includes(i) ? 1 : 0 });
        setOrder(order);
    });
    E.sheet.addEventListener('click', onSheetClick);
    E.restore.addEventListener('click', () => {
        ae('pages_restore', {});
        setOrder(S.sizes.map((s) => s.index));
        if (S.trim) findInk();
    });

    E.go.addEventListener('click', onGo);
    E.share.addEventListener('click', onShare);
    E.folder.addEventListener('click', saveToFolder);

    // Re-fit the preview when the stage changes width (rotation, sidebar).
    if ('ResizeObserver' in window) {
        let last = 0;
        new ResizeObserver(() => {
            const w = E.view.clientWidth;
            if (Math.abs(w - last) < 8) return;
            last = w;
            if (S.doc && S.mode === 'sheet') buildSheet();
        }).observe(E.view);
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
